import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) {
    throw new Error(body?.message || 'EOAF request failed');
  }
  return body.data;
}

export type EoafActionSummary = {
  id: string;
  title: string;
  status: string;
  priority: string;
  severity: string;
  domain: string;
  department?: string | null;
  ownerUserId?: string | null;
  ownerRole?: string | null;
  dueDate?: string | null;
  sourceType: string;
  relatedRecommendationId?: string | null;
  estimatedBenefit?: string | null;
  effectivenessScore?: number | null;
  createdAt?: string;
};

export type EoafDashboard = {
  myActions: EoafActionSummary[];
  critical: EoafActionSummary[];
  overdue: EoafActionSummary[];
  openCount: number;
  criticalCount: number;
  overdueCount: number;
  completedCount: number;
  byDepartment: Record<string, number>;
  byPriority: Record<string, number>;
  dispositions: Record<string, number>;
  effectiveness: Record<string, number>;
  workload: { byUser: unknown[]; byDepartment: unknown[] };
  financialExposureHint?: number;
  degrade?: { jobs_disabled?: boolean };
  refreshed_at?: string;
};

export const eoafService = {
  getDashboard: () => unwrap<EoafDashboard>(api.get('/eoaf/dashboard')),
  listActions: (params?: Record<string, string>) => {
    const qs = params ? `?${new URLSearchParams(params)}` : '';
    return unwrap<EoafActionSummary[]>(api.get(`/eoaf/actions${qs}`));
  },
  getAction: (id: string) => unwrap<Record<string, unknown>>(api.get(`/eoaf/actions/${id}`)),
  createAction: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/eoaf/actions', payload)),
  convertFromEoip: (recommendationId: string, disposition = 'accepted', reason?: string) =>
    unwrap<unknown>(
      api.post('/eoaf/actions', {
        sourceType: 'eoip_recommendation',
        sourceId: recommendationId,
        disposition,
        dispositionReason: reason,
      })
    ),
  patchAction: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/eoaf/actions/${id}`, payload)),
  accept: (id: string) => unwrap<unknown>(api.post(`/eoaf/actions/${id}/accept`, {})),
  start: (id: string) => unwrap<unknown>(api.post(`/eoaf/actions/${id}/start`, {})),
  pause: (id: string) => unwrap<unknown>(api.post(`/eoaf/actions/${id}/pause`, {})),
  complete: (id: string, payload?: Record<string, unknown>) =>
    unwrap<unknown>(api.post(`/eoaf/actions/${id}/complete`, payload || {})),
  comment: (id: string, body: string) =>
    unwrap<unknown>(api.post(`/eoaf/actions/${id}/comment`, { body })),
  outcome: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post(`/eoaf/actions/${id}/outcome`, payload)),
  completeStep: (id: string, stepId: string) =>
    unwrap<unknown>(api.post(`/eoaf/actions/${id}/steps/${stepId}/complete`, {})),
  listPlaybooks: () => unwrap<unknown[]>(api.get('/eoaf/playbooks')),
  createPlaybook: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/eoaf/playbooks', payload)),
  getEffectiveness: () => unwrap<Record<string, number>>(api.get('/eoaf/effectiveness')),
  getWorkload: () => unwrap<Record<string, unknown>>(api.get('/eoaf/workload')),
  getEscalations: () => unwrap<unknown[]>(api.get('/eoaf/escalations')),
};
